const STORAGE_PREFIX = 'teo_launcher_read_ids:';
const LEGACY_BASELINE_PREFIX = 'teo_launcher_read_baseline:';
const ACK_GREETING_PREFIX = 'teo_launcher_ack_greetings:';
const MAX_STORED_IDS = 400;
const MAX_ACK_PHRASES = 80;

/** Fired when greeting phrases are acknowledged for launcher unread. */
export const LAUNCHER_ACK_GREETINGS_EVENT = 'teo-launcher-ack-greetings';

function sessionStore(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function storageKey(slug: string, threadId: string | number): string {
  return `${STORAGE_PREFIX}${slug}:${threadId}`;
}

function legacyBaselineKey(slug: string, threadId: string | number): string {
  return `${LEGACY_BASELINE_PREFIX}${slug}:${threadId}`;
}

function ackGreetingKey(slug: string): string {
  return `${ACK_GREETING_PREFIX}${slug}`;
}

export function normalizeLauncherPhraseText(text: string): string {
  return text.trim().replace(/\s+/g, ' ');
}

/** Message ids the visitor has already "seen" for launcher unread. */
export function readLauncherReadMessageIds(
  slug: string,
  threadId: string | number,
): string[] {
  const storage = sessionStore();
  if (!storage) return [];
  try {
    const raw = storage.getItem(storageKey(slug, threadId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((id): id is string => typeof id === 'string' && id.length > 0);
  } catch {
    return [];
  }
}

export function writeLauncherReadMessageIds(
  slug: string,
  threadId: string | number,
  ids: Iterable<string>,
): void {
  const storage = sessionStore();
  if (!storage) return;
  try {
    const unique = [...new Set(ids)].filter((id) => id.length > 0);
    const trimmed = unique.slice(-MAX_STORED_IDS);
    storage.setItem(storageKey(slug, threadId), JSON.stringify(trimmed));
    // Drop legacy count baseline if present.
    storage.removeItem(legacyBaselineKey(slug, threadId));
  } catch {
    // ignore quota / private mode
  }
}

/**
 * Greeting phrases already shown to the visitor (popup / welcome / pending).
 * When the backend later injects the same text into the thread, launcher must
 * not count them as unread.
 */
export function readLauncherAckGreetingPhrases(slug: string): string[] {
  const storage = sessionStore();
  if (!storage) return [];
  try {
    const raw = storage.getItem(ackGreetingKey(slug));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (text): text is string => typeof text === 'string' && text.length > 0,
    );
  } catch {
    return [];
  }
}

export function acknowledgeLauncherGreetingPhrases(
  slug: string,
  phrases: Iterable<string>,
): void {
  const incoming = [...phrases]
    .map(normalizeLauncherPhraseText)
    .filter((text) => text.length > 0);
  if (incoming.length === 0) return;

  const storage = sessionStore();
  if (!storage) return;

  const merged = [...readLauncherAckGreetingPhrases(slug), ...incoming];
  const unique = [...new Set(merged)].slice(-MAX_ACK_PHRASES);
  try {
    storage.setItem(ackGreetingKey(slug), JSON.stringify(unique));
  } catch {
    // ignore
  }

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(LAUNCHER_ACK_GREETINGS_EVENT));
  }
}

/** Clears launcher unread state (playground / new-chat test). */
export function clearLauncherUnreadBaselines(slug?: string): string[] {
  const storage = sessionStore();
  if (!storage) return [];

  const keys: string[] = [];
  for (let i = 0; i < storage.length; i++) {
    const key = storage.key(i);
    if (
      key &&
      (key.startsWith(STORAGE_PREFIX) ||
        key.startsWith(LEGACY_BASELINE_PREFIX) ||
        key.startsWith(ACK_GREETING_PREFIX)) &&
      (!slug || key.includes(slug))
    ) {
      keys.push(key);
    }
  }
  for (const key of keys) {
    storage.removeItem(key);
  }
  return keys;
}

/** @deprecated Use readLauncherReadMessageIds — count baseline is unreliable. */
export function readLauncherUnreadBaseline(
  slug: string,
  threadId: string | number,
): number | null {
  const storage = sessionStore();
  if (!storage) return null;
  try {
    const raw = storage.getItem(legacyBaselineKey(slug, threadId));
    if (raw == null) return null;
    const value = Number(raw);
    if (!Number.isFinite(value) || value < 0) return null;
    return Math.floor(value);
  } catch {
    return null;
  }
}

/** @deprecated Use writeLauncherReadMessageIds. */
export function writeLauncherUnreadBaseline(
  slug: string,
  threadId: string | number,
  count: number,
): void {
  const storage = sessionStore();
  if (!storage) return;
  if (!Number.isFinite(count) || count < 0) return;
  try {
    storage.setItem(legacyBaselineKey(slug, threadId), String(Math.floor(count)));
  } catch {
    // ignore
  }
}
